/**
 * Utilitaire pour formater les dates au style français (jour/mois/année heure:minute)
 * avec les heures sur 24h (sans AM/PM)
 */

/**
 * Formate une date au format français (DD/MM/YYYY HH:MM)
 * @param {Date|string|number} date - Date à formater (objet Date, timestamp ou chaîne ISO)
 * @param {Object} options - Options de formatage
 * @param {boolean} options.withTime - Inclure l'heure dans le format (par défaut: true)
 * @param {boolean} options.withSeconds - Inclure les secondes dans le format (par défaut: false)
 * @returns {string} - Date formatée
 */
 export const formatDateFR = (date, options = {}) => {
    try {
      // Valeurs par défaut des options
      const { withTime = true, withSeconds = false } = options;
      
      // S'assurer que la date est un objet Date valide
      const dateObj = date instanceof Date ? date : new Date(date);
      
      // Vérifier si la date est valide
      if (isNaN(dateObj.getTime())) {
        return 'Date invalide';
      }
      
      // Formatage de la date en français (DD/MM/YYYY)
      const formatter = new Intl.DateTimeFormat('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: withTime ? '2-digit' : undefined,
        minute: withTime ? '2-digit' : undefined,
        second: withTime && withSeconds ? '2-digit' : undefined,
        hour12: false // Format 24h
      });
      
      return formatter.format(dateObj);
    } catch (error) {
      console.error('Erreur lors du formatage de la date:', error);
      return 'Date invalide';
    }
  };
  
  /**
   * Formate une date et heure de création pour les affichages détaillés
   * @param {Date|string|number} date - Date à formater
   * @returns {string} - "Créé le JJ/MM/YYYY à HH:MM"
   */
  export const formatCreationDate = (date) => {
    try {
      const formattedDate = formatDateFR(date);
      return `Créé le ${formattedDate.replace(' ', ' à ')}`;
    } catch (error) {
      console.error('Erreur lors du formatage de la date de création:', error);
      return 'Date de création invalide';
    }
  };
  
  /**
   * Formate une date relative (aujourd'hui, hier, cette semaine, etc.)
   * @param {Date|string|number} date - Date à formater
   * @returns {string} - Date relative ou date au format français
   */
  export const formatRelativeDate = (date) => {
    try {
      const dateObj = date instanceof Date ? date : new Date(date);
      
      // Vérifier si la date est valide
      if (isNaN(dateObj.getTime())) {
        return 'Date invalide';
      }
      
      const now = new Date();
      const diffInMilliseconds = now - dateObj;
      const diffInHours = diffInMilliseconds / (1000 * 60 * 60);
      const diffInDays = diffInHours / 24;
      
      // Aujourd'hui
      if (dateObj.toDateString() === now.toDateString()) {
        const formatter = new Intl.DateTimeFormat('fr-FR', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        });
        return `Aujourd'hui à ${formatter.format(dateObj)}`;
      }
      
      // Hier
      const yesterday = new Date();
      yesterday.setDate(now.getDate() - 1);
      if (dateObj.toDateString() === yesterday.toDateString()) {
        const formatter = new Intl.DateTimeFormat('fr-FR', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        });
        return `Hier à ${formatter.format(dateObj)}`;
      }
      
      // Cette semaine (moins de 7 jours)
      if (diffInDays < 7) {
        const formatter = new Intl.DateTimeFormat('fr-FR', {
          weekday: 'long',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        });
        return formatter.format(dateObj);
      }
      
      // Plus de 7 jours : format standard
      return formatDateFR(dateObj);
    } catch (error) {
      console.error('Erreur lors du formatage de la date relative:', error);
      return 'Date invalide';
    }
  };
  
  const dateFormatter = {
    formatDateFR,
    formatCreationDate,
    formatRelativeDate
  };

  export default dateFormatter;